import clientData from "@/services/clientData";
import get from "lodash/get";
import difference from "lodash/difference";
import cloneDeep from "lodash/cloneDeep";
import keyBy from "lodash/keyBy";
import {
    isNotBlank,
    hasNoEmojis,
    isOnlyNumeric,
    isPhoneNumber,
    isEmail,
    isYesOrNo,
    isFloating,
    isLessThanOrEqualTo800,
    isLessThanOrEqualTo36,
    isValidGradYear,
    isValidDate,
    hasNoAtSign,
    isValidAthleteName,
} from "@/utils/validators";
import Vue from "vue";
import {athlete} from "../athleteProfile/getters";

export const clubId = () => clientData("user_club_id");
export const importId = (state) => state.import.id;
export const club = () => ({
    name: clientData("user_club_name"),
    sport: clientData("sport_name"),
    gender: clientData("platform_gender") === 1 ? "Men's" : "Women's",
    type: clientData("user_club_organization_type"),
});

export const currentPage = (state) => {
    return state.pages[state.activeStep];
};

export const isLastPage = (state) => {
    return state.activeStep === state.pages.length - 1;
};

export const filename = (state) => state.import.file_name || state.import.name;

export const createdAt = (state) => state.import.created_at;

export const editDataLength = (state) => state.editData.length;

export const parsedCsvObjLength = (state) =>
    state.parsedCsv.data && Object.entries(state.parsedCsv.data[0]).length;

export const rowLength = (state) =>
    state.parsedCsv.data && state.parsedCsv.data.length + (-state.removedEditData.length);

export const groupedEntries = (state, getters) => {
    let groupedObj = {};
    let matchedColumns = Object.entries(state.matchedColumns);
    matchedColumns.forEach(([key, val]) => {
        if (getters.ignoreColumnsList.includes(key)) return;
        groupedObj[val] = [];
        state.editData.forEach((entry) => {
            if (state.removedEditData.includes(entry.id)) return;
            if (val === entry.column) groupedObj[val].push(entry);
        });
    });

    return groupedObj;
};

export const getInvalidCounts = (state, getters) => {
    let counts = {};

    for (let key in getters.groupedEntries) {
        counts[key] = getters.groupedEntries[key].filter(
            (entry) => entry.status === "invalid"
        ).length;
    }
    return counts;
};

export const unmatchedTeamsCount = (state) => {
    if (
        "team_assignment" in state.matchedColumns &&
        state.unique[state.matchedColumns.team_assignment]
    )
        return (
            state.unique[state.matchedColumns.team_assignment].filter((t) =>
                ["unmatched", "unknown"].includes(t.status)
            ).length || 0
        );
    return 0;
};

export const unmatchedPositionsCount = (state) => {
    if (
        "position" in state.matchedColumns &&
        state.unique[state.matchedColumns.position]
    )
        return (
            state.unique[state.matchedColumns.position].filter((p) =>
                ["unmatched", "unknown"].includes(p.status)
            ).length || 0
        );
    return 0;
};

export const globalProcess = (state) => {
    return state.processingAll.editData.status;
};

export const isLoaded = (state) => get(state, "import.id") > 0;

export const isRequiredColumnsMatched = (state) => {
    return true;
};

export const matchedColumnsList = (state) => {
    return Object.values(state.matchedColumns);
};

export const matchedColumnsReversed = (state) => {
    return Object.entries(state.matchedColumns).reduce((acc, [key, val]) => {
        acc[val] = key;
        return acc;
    }, {});
};

export const ignoreColumnsList = (state) => {
    return ["team_assignment", "position"];
};

export const totalErrors = (state) => {
    return state.editData.filter(
        (entry) =>
            !state.removedEditData.includes(entry.id) && entry.status === "invalid"
    ).length;
};

export const requiredErrors = (state, getters) => {
    const requiredList = getters.requiredColumns.map((col) => col.value);
    return state.editData.filter(
        (entry) =>
            !state.removedEditData.includes(entry.id) &&
            requiredList.includes(getters.matchedColumnsReversed[entry.column]) &&
            entry.status === "invalid"
    ).length;
};

export const athleteNames = (state) => {
    const firstNames = state.editData.filter(
        (entry) => entry.column === state.matchedColumns.first_name
    );
    const lastNames = state.editData.filter(
        (entry) => entry.column === state.matchedColumns.last_name
    );
    const athleteNames = firstNames.reduce((acc, name, index) => {
        acc[name.id] = `${name.value} ${lastNames[index].value}`;
        return acc;
    }, {});
    return athleteNames;
};

export const gradYears = (state) => {
    const gradYears = state.editData.filter(
        (entry) => entry.column === state.matchedColumns.grad_year
    );
    return gradYears.reduce((acc, {id, value}) => {
        acc[id] = `${value}`;
        return acc;
    }, {});
};

// export const existingEmails = (state) => {
//   const emails = state.existingAthletes.reduce((acc, athlete) => {
//     let [playerEmail] = athlete.siteAccesses.filter(
//       (e) => e.email_type === "player"
//     );
//     acc.push(playerEmail.siteEmail.email);
//     return acc;
//   }, []);
//   return emails;
// };

export const existingAthletes = (state) => {
    return state.existingAthletes;
};
export const finalRosterById = (state, {finalRoster}) => {
    return keyBy(finalRoster, (athlete) => athlete.id || athlete.uuid);
};

export const removedAthletesList = (state) => {
    return state.removedFromRoster.reduce((acc, entry) => {
        if ("id" in entry) acc.push(entry.id);
        return acc;
    }, []);
};

export const requiredColumns = (state) => {
    return state.suggestedColumns.filter((col) => col.required);
};

export const suggestedColumnLabels = (state) => {
    return state.suggestedColumns.reduce((acc, {label, value}) => {
        acc[value] = label;
        return acc;
    }, {});
};

export const canProceed = (state, getters) => {
    if (! state.hasHydration) return false;

    let columns;
    if (getters.currentPage.name === "clubimport.columns") {
        let matchedKeys = Object.keys(state.matchedColumns);
        columns = getters.requiredColumns
            .map((col) => matchedKeys.includes(col.value))
            .every((e) => e === true);
    }

    let match;
    if (getters.currentPage.name === "clubimport.match") {
        let hasTeams = "team_assignment" in state.matchedColumns;
        let hasPositions = "position" in state.matchedColumns;
        let allTeamsMatched = getters.unmatchedTeamsCount === 0;
        let allPositionsMatched = getters.unmatchedPositionsCount === 0;
        if (hasTeams && hasPositions)
            match = allTeamsMatched && allPositionsMatched;
        if (hasTeams && !hasPositions) match = allTeamsMatched;
        if (hasPositions && !hasTeams) match = allPositionsMatched;
        if (!hasPositions && !hasTeams) match = true;
    }

    return {
        "clubimport.columns": !state.disableContinueButton && columns,
        "clubimport.match": !state.disableContinueButton && match,
        "clubimport.clean": !state.disableContinueButton && getters.totalErrors === 0,
        "clubimport.review": !state.disableContinueButton && true,
    };
};

export const canGoBack = (state, getters) => {
    return getters.currentPage.name !== "clubimport.columns" && getters.currentPage.name !== "clubimport.review";
};

export const validators = () => {
    return {
        first_name: [isNotBlank, hasNoEmojis, isValidAthleteName],
        last_name: [isNotBlank, hasNoEmojis, isValidAthleteName],
        athlete_email: [isNotBlank, isEmail],
        grad_year: [isNotBlank, isOnlyNumeric, isValidGradYear],
        team_assignment: [],
        jersey_number: [isOnlyNumeric],
        position: [],
        athlete_phone: [isPhoneNumber],
        guardian_1_first_name: [hasNoEmojis],
        guardian_1_last_name: [hasNoEmojis],
        guardian_1_email: [isEmail],
        guardian_1_phone: [isPhoneNumber],
        guardian_2_first_name: [hasNoEmojis],
        guardian_2_last_name: [hasNoEmojis],
        guardian_2_email: [isEmail],
        guardian_2_phone: [isPhoneNumber],
        birthdate: [isValidDate],
        "height_(in)": [isOnlyNumeric],
        weight: [isOnlyNumeric],
        gpa: [isFloating],
        act: [isOnlyNumeric, isLessThanOrEqualTo36],
        sat_math: [isOnlyNumeric, isLessThanOrEqualTo800],
        sat_verbal: [isOnlyNumeric, isLessThanOrEqualTo800],
        is_committed: [isYesOrNo],
        zip: [isOnlyNumeric],
        address_1: [hasNoEmojis],
        city: [hasNoEmojis],
        state: [hasNoEmojis],
        twitter: [hasNoEmojis, hasNoAtSign],
        instagram: [hasNoEmojis, hasNoAtSign],
    };
};

// Final Review

export const transformedExistingAthletes = (state, getters) => {
    const athletes = getters.existingAthletes.reduce((acc, athlete) => {
        let tempAthlete = {};
        //name
        tempAthlete[state.matchedColumns.first_name] = athlete.first_name;
        tempAthlete[state.matchedColumns.last_name] = athlete.last_name;

        // teams
        tempAthlete[state.matchedColumns.team_assignment] = athlete.teams || null;
        // email
        const email = /,/.test(athlete.email)
            ? athlete.email.split(",")[0]
            : athlete.email;
        tempAthlete[state.matchedColumns.athlete_email] = email;
        // gradyear
        tempAthlete[state.matchedColumns.grad_year] = athlete.grad_year;
        // id
        tempAthlete.id = athlete.id;
        acc.push(tempAthlete);
        return acc;
    }, []);

    return athletes;
};

export const combinedRosters = (
    state,
    {athleteArr, transformedExistingAthletes}
) => {
    let filteredExistingAthletes = transformedExistingAthletes.filter(
        (athlete) => {
            return !athleteArr.some((ath) => ath.user_id === athlete.id);
        }
    );
    return [...athleteArr, ...filteredExistingAthletes];
};

// This is Array we use on the final review tab / draw comparisons from.  It's derived from combinedRosters which combines the existing athletes (transformed to same data shape) and the CSV athletes

export const finalRoster = (state, {combinedRosters}) => {
    if (!state.removedFromRoster.length) return combinedRosters;
    return difference(combinedRosters, state.removedFromRoster);
};

export const totalNew = (state) => {
    return state.athleteArr.filter((a) => a.status !== "updated").length;
};
export const totalUpdated = (state) => {
    return state.athleteArr.filter((a) => a.status === "updated").length;
};

// Upload / Submit Roster Getters

export const teamKey = (state) => state.matchedColumns.team_assignment || "";
export const positionKey = (state) => state.matchedColumns.position || "";

export const clubTeamsKeyedByValue = (state) =>
    state.clubTeams.reduce((acc, team) => {
        acc[team.value] = {...team};
        return acc;
    }, {});

export const clubTeamsKeyedById = (state) =>
    state.clubTeams.reduce((acc, team) => {
        acc[team.id] = {...team};
        return acc;
    }, {});

export const teamsList = (state, {teamKey}) =>
    (teamKey &&
        state.unique[teamKey] &&
        state.unique[teamKey].map((team) => team.value)) ||
    [];

export const teamsMatch = (state, {teamKey, clubTeamsKeyedByValue}) => {
    return (
        (teamKey &&
            state.unique[teamKey] &&
            state.unique[teamKey].reduce((acc, team) => {
                team.status === "matched" &&
                (acc[team.value] = clubTeamsKeyedByValue[team.matchedValue].id);
                return acc;
            }, {})) ||
        {}
    );
};
export const teamsSkipped = (state, {teamKey}) => {
    return (
        (teamKey &&
            state.unique[teamKey] &&
            state.unique[teamKey].reduce((acc, team) => {
                team.status === "skipped" && acc.push(team.value);
                return acc;
            }, [])) ||
        []
    );
};

export const teamsNew = (state, {teamKey}) => {
    return (
        (teamKey &&
            state.unique[teamKey] &&
            state.unique[teamKey].reduce((acc, team) => {
                team.status === "created" && acc.push(team.value);
                return acc;
            }, [])) ||
        []
    );
};

export const positionsList = (state, {positionKey}) => {
    let list = new Set();
    state.unique[positionKey] &&
    state.unique[positionKey].forEach((p) => list.add(p.value));
    return Array.from(list) || [];
};
export const positionsMatch = (state, {positionKey}) => {
    if (!state.unique[positionKey]) return {};
    return state.unique[positionKey].reduce((acc, position) => {
        position.status === "matched" &&
        (acc[position.value] = position.matchedValue);
        return acc;
    }, {});
};

export const athleteObj = (state, {matchedColumnsList, teamKey}) => {
    let obj = {};
    if (state.hasHydration && state.editData && state.editData.length) {
        let editData = cloneDeep(state.editData) || [];
        editData.forEach((entry) => {
            // skip removed data
            if (state.removedEditData.includes(entry.id)) return;
            // if it's not in the matched column list, skip it, except for the exceptions listed in the array.
            if (
                !matchedColumnsList.includes(entry.column) &&
                !["is_club_member", "user_id", "deleted_at"].includes(entry.column)
            )
                return;

            // setup the object key by uuid
            if (!obj[entry.id]) {
                obj[entry.id] = {
                    uuid: entry.id,
                };
            }
            if (entry.column === "deleted_at") {
                obj[entry.id].deleted = !!entry.value;
            }
            // unassign skipped teams
            if (teamKey && state.unique[teamKey] && entry.column === teamKey) {
                state.unique[teamKey].forEach((team) => {
                    if (team.status === "unknown" || team.status === "created") return;
                    if (team.status === "skipped") {
                        if (
                            Array.isArray(entry.value) &&
                            entry.value.includes(team.value)
                        ) {
                            entry.value = entry.value.filter((t) => t !== team.value);
                        } else if (entry.value === team.value) {
                            entry.value = "";
                        }
                    }
                });
            }
            obj[entry.id][entry.column] = entry.value;
        });
    }
    return obj;
};

export const athleteArr = (state, {athleteObj}) => Object.values(athleteObj);

export const athleteArrHasDeleted = (state, {athleteArr}) => {
    return athleteArr.some((a) => a.deleted);
};

export const rowsWithMissingAthleteEmail = (state, getters) => {
    if (!state.matchedColumns.athlete_email) {
        return [];
    }
    
    return getters.athleteArr.filter((athlete) => {
        return !athlete[state.matchedColumns.athlete_email];
    });
};

export const athletesWithMissingEmailWithValidGuardianEmail = (state, getters) => {
    let hasGuardian1EmailColumn = state.matchedColumns.hasOwnProperty('guardian_1_email');
    let hasGuardian2EmailColumn = state.matchedColumns.hasOwnProperty('guardian_2_email');
    
    return getters.rowsWithMissingAthleteEmail.filter((athlete) => {
        return (hasGuardian1EmailColumn && isNotBlank(athlete[state.matchedColumns.guardian_1_email]) && isEmail(athlete[state.matchedColumns.guardian_1_email])) ||
        (hasGuardian2EmailColumn && isNotBlank(athlete[state.matchedColumns.guardian_2_email]) && isEmail(athlete[state.matchedColumns.guardian_2_email]));
    });
};

export const alertMessageForAthletesWithMissingEmails = (state, getters) => {
    let count = getters.athletesWithMissingEmailWithValidGuardianEmail.length;
    let isPlural = count !== 1;
    
    return `We found ${isPlural ? `${count} athletes` : '1 athlete'} with an empty athlete email who ${isPlural ? 'have' : 'has'} a valid guardian email address.
        You can resolve ${isPlural ? 'these errors' : 'this error'} by moving the guardian email to the associated athlete email all at once.`;
};

export const modalMessageForAthletesWithMissingEmails = (state) => {
    let count = state.totalAthletesWithMissingEmailsBeingResolved;
    let isPlural = count !== 1;
    
    return `Moving ${isPlural ? `${count} guardian emails` : '1 guardian email' } to athlete ${isPlural ? 'emails' : 'email'}...`;
};

export const importUploadErrorMessages = (state) => {
    let result = [];
    let messages = {
        'invalid-headers': "File cannot contain columns without headings. Please add a heading to all columns in your .csv, or delete the columns without headings, and re-upload to continue."
    };
    
    state.importUploadErrors.forEach(error => {
        if (messages[error]) {
            result.push(messages[error]);
        }
    });
    
    return result;
};